import { useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { PageHeader, EmptyState } from "@/components/Common";
import { toast } from "sonner";
import { Plus, Users, Pencil, Trash2, MapPin, Mail, Phone } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";

const blank = {
  name: "", contact_person: "", email: "", phone: "", abn: "",
  billing_address: "", job_address: "", notes: "",
};

const Field = ({ label, value, onChange, testid, placeholder, type = "text" }) => (
  <label className="block">
    <span className="text-xs font-medium text-slate-600">{label}</span>
    <input
      data-testid={testid}
      type={type}
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full border border-slate-200 rounded-md px-3 py-2 text-sm text-slate-900 bg-white placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
    />
  </label>
);

export default function Clients() {
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);

  const load = async () => {
    const { data } = await api.get("/clients");
    setClients(data);
  };
  useEffect(() => { load().catch(() => {}); }, []);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ ...blank, ...c }); setOpen(true); };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Client name is required");
    const payload = { ...blank, ...form };
    delete payload.id; delete payload.created_at;
    try {
      if (editing) await api.put(`/clients/${editing.id}`, payload);
      else await api.post("/clients", payload);
      toast.success(editing ? "Client updated" : "Client added");
      setOpen(false);
      load();
    } catch {
      toast.error("Could not save client");
    }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}?`)) return;
    await api.delete(`/clients/${c.id}`);
    toast.success("Client deleted");
    load();
  };

  return (
    <div data-testid="clients-page">
      <PageHeader title="Clients" subtitle="Contacts and job addresses for quoting">
        <button data-testid="add-client-btn" onClick={openNew}
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 text-sm font-medium transition-colors active:scale-95">
          <Plus size={16} /> Add Client
        </button>
      </PageHeader>

      {clients.length === 0 ? (
        <EmptyState icon={Users} title="No clients yet" description="Add a client to attach to your quotes."
          action={<button onClick={openNew} data-testid="empty-add-client-btn"
            className="text-sm font-medium text-blue-600">Add your first client →</button>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {clients.map((c) => (
            <div key={c.id} data-testid={`client-card-${c.id}`}
              className="bg-white border border-slate-200 shadow-sm rounded-md p-5 hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{c.name}</p>
                  {c.contact_person && <p className="text-xs text-slate-500 mt-0.5">{c.contact_person}</p>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button data-testid={`edit-client-${c.id}`} onClick={() => openEdit(c)} aria-label="Edit client"
                    className="p-1.5 rounded-md text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors">
                    <Pencil size={15} />
                  </button>
                  <button data-testid={`delete-client-${c.id}`} onClick={() => remove(c)} aria-label="Delete client"
                    className="p-1.5 rounded-md text-slate-400 hover:bg-red-50 hover:text-red-600 transition-colors">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
              <div className="mt-4 space-y-1.5 text-xs text-slate-600">
                {c.email && <p className="flex items-center gap-2"><Mail size={13} className="text-slate-400" />{c.email}</p>}
                {c.phone && <p className="flex items-center gap-2"><Phone size={13} className="text-slate-400" />{c.phone}</p>}
                {c.job_address && <p className="flex items-start gap-2"><MapPin size={13} className="text-slate-400 mt-0.5" />{c.job_address}</p>}
                {c.abn && <p className="text-slate-400 font-mono">ABN {c.abn}</p>}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-lg max-h-[90vh] overflow-y-auto" data-testid="client-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: "Manrope, sans-serif" }}>
              {editing ? "Edit client" : "Add client"}
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
            <div className="sm:col-span-2">
              <Field label="Client / business name *" value={form.name} onChange={set("name")}
                testid="client-name-input" placeholder="Acme Builders Pty Ltd" />
            </div>
            <Field label="Contact person" value={form.contact_person} onChange={set("contact_person")} testid="client-contact-input" />
            <Field label="ABN" value={form.abn} onChange={set("abn")} testid="client-abn-input" placeholder="12 345 678 901" />
            <Field label="Email" value={form.email} onChange={set("email")} testid="client-email-input" type="email" />
            <Field label="Phone" value={form.phone} onChange={set("phone")} testid="client-phone-input" />
            <div className="sm:col-span-2">
              <Field label="Billing address" value={form.billing_address} onChange={set("billing_address")} testid="client-billing-input" />
            </div>
            <div className="sm:col-span-2">
              <Field label="Job address" value={form.job_address} onChange={set("job_address")} testid="client-job-address-input"
                placeholder="42 Bourke St, Melbourne VIC 3000" />
            </div>
          </div>
          <DialogFooter>
            <button onClick={() => setOpen(false)}
              className="bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 rounded-md px-4 py-2 text-sm font-medium transition-colors">
              Cancel
            </button>
            <button data-testid="save-client-btn" onClick={save}
              className="bg-blue-600 hover:bg-blue-700 text-white rounded-md px-4 py-2 text-sm font-medium transition-colors">
              Save client
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
